import type {DocumentNode} from 'graphql';

import type {
    CoreEntityTaskFragment,
    GetActivityForModal,
    GetActivityForModalVariables,
    GetTasksForLead,
    GetTasksForLeadVariables,
    GetTasksForPeep,
    GetTasksForPeepVariables,
} from 'nutshell-graphql-types';

import GET_ACTIVITY_FOR_MODAL from '../../../../../activity-modal/graphql/queries/get-activity-for-modal.graphql';
import GET_TASKS_FOR_LEAD from '../../../../../lead/content/tasks-widget/graphql/queries/get-tasks-for-lead.graphql';
import GET_TASKS_FOR_PEEP from '../../../../../peep/shared/tasks-widget/graphql/queries/get-tasks-for-peep.graphql';

interface CacheProxy {
    readQuery<TData, TVariables = Record<string, string>>(options: {
        query: DocumentNode;
        variables?: TVariables;
    }): TData | null;
    writeQuery<TData, TVariables = Record<string, string>>(options: {
        query: DocumentNode;
        variables?: TVariables;
        data: TData;
    }): void;
}

/**
 * Removes a deleted task from all relevant Apollo cache queries in a single pass.
 *
 * Rather than needing an array of refetchQueries, we always try to update any relevant
 * queries which may be in cache, so we don't need to pass a list of different queries from multiple different call sites.
 * Determines which queries to update based on the task's relatedEntity and creatorEntity.
 *
 * - relatedEntity.id is the lead or peep the task belongs to.
 *
 * - creatorEntity.id is the activity that created this task (AI followup task).
 *   If the activity modal is open, GetActivityForModal will be in cache and needs updating too.
 *   This handles the case where the same task is visible in both the tasks widgets and the activity modal simultaneously.
 */
export function removeTaskFromCaches(
    proxy: CacheProxy,
    taskId: string,
    cachedTask: CoreEntityTaskFragment
): void {
    const relatedEntity = cachedTask.relatedEntity;
    const creatorEntity = cachedTask.creatorEntity;

    if (relatedEntity) {
        if (relatedEntity.__typename === 'Lead') {
            removeFromLeadCache(proxy, taskId, relatedEntity.id);
        } else if (
            relatedEntity.__typename === 'Account' ||
            relatedEntity.__typename === 'Contact'
        ) {
            removeFromPeepCache(proxy, taskId, relatedEntity.id);
        }
    }

    if (creatorEntity?.__typename === 'Activity') {
        removeFromActivityCache(proxy, taskId, creatorEntity.id);
    }
}

function removeFromLeadCache(proxy: CacheProxy, taskId: string, leadId: string): void {
    try {
        const cached = proxy.readQuery<GetTasksForLead, GetTasksForLeadVariables>({
            query: GET_TASKS_FOR_LEAD,
            variables: {leadId},
        });

        if (!cached?.lead || !cached.lead.tasks) return;

        proxy.writeQuery<GetTasksForLead, GetTasksForLeadVariables>({
            query: GET_TASKS_FOR_LEAD,
            variables: {leadId},
            data: {
                lead: {
                    ...cached.lead,
                    tasks: cached.lead.tasks.filter((task) => task.id !== taskId),
                },
            },
        });
    } catch {
        // may throw when opening activity modal on other pages and removing followup task
    }
}

function removeFromPeepCache(proxy: CacheProxy, taskId: string, peepId: string): void {
    try {
        const cached = proxy.readQuery<GetTasksForPeep, GetTasksForPeepVariables>({
            query: GET_TASKS_FOR_PEEP,
            variables: {peepId},
        });

        if (!cached?.peep || !cached.peep.tasks) return;

        proxy.writeQuery<GetTasksForPeep, GetTasksForPeepVariables>({
            query: GET_TASKS_FOR_PEEP,
            variables: {peepId},
            data: {
                peep: {
                    ...cached.peep,
                    tasks: cached.peep.tasks.filter((task) => task.id !== taskId),
                },
            },
        });
    } catch {
        // may throw when opening activity modal on other pages and removing followup task
    }
}

function removeFromActivityCache(proxy: CacheProxy, taskId: string, activityId: string): void {
    try {
        const cached = proxy.readQuery<GetActivityForModal, GetActivityForModalVariables>({
            query: GET_ACTIVITY_FOR_MODAL,
            variables: {activityId},
        });

        if (!cached?.activity) return;

        proxy.writeQuery<GetActivityForModal, GetActivityForModalVariables>({
            query: GET_ACTIVITY_FOR_MODAL,
            variables: {activityId},
            data: {
                activity: {
                    ...cached.activity,
                    tasks: cached.activity.tasks.filter((task) => task.id !== taskId),
                },
            },
        });
    } catch {
        // throws if we delete an AI folowup task but activity modal isn't open
    }
}
